import React, { useState } from "react";
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  CircularProgress, 
  Paper,
  InputAdornment,
  IconButton,
  Link,
  Alert,
  Container,
  Card,
  CardContent,
  useTheme
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useClerk, useSignIn } from "@clerk/clerk-react";
import Header from "../common/Header";
import Footer from "../common/Footer";
import { Visibility, VisibilityOff, LockOutlined } from "@mui/icons-material";
import { useAppSettings } from "../../contexts/AppSettingsContext";
import { API_URL } from "../../config";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect') || '/dashboard';
  const theme = useTheme();
  const { settings } = useAppSettings();
  
  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  
  // Password recovery states
  const [isRecoveringPassword, setIsRecoveringPassword] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  
  // Clerk hooks
  const { signIn, isLoaded: isSignInLoaded } = useSignIn();
  const clerk = useClerk();

  // Toggle password visibility
  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  // Validate login form
  const validateLoginForm = (): boolean => {
    const errors: Record<string, string> = {};
    let isValid = true;

    if (!email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      errors.email = 'Invalid email address';
      isValid = false;
    }

    if (!password) {
      errors.password = 'Password is required';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  // Check if user exists in local database or has pending invitation
  const checkUserAccess = async (clerkUserId: string, userEmail: string, token: string): Promise<boolean> => {
    try {
      console.log(`Checking user access for ${userEmail} (Clerk ID: ${clerkUserId})`);
      
      // First check if user exists in local database
      const userResponse = await fetch(`${API_URL}/users/check-exists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          clerkId: clerkUserId,
          email: userEmail 
        })
      });
      
      if (userResponse.ok) {
        const userData = await userResponse.json();
        console.log('User check response:', userData);
        
        if (userData.exists) {
          console.log('User exists in local database');
          return true; // User exists in local database
        }
      } else {
        console.error('Error checking if user exists:', await userResponse.text());
      }
      
      // Try a fallback check by email only if the first check failed
      try {
        console.log('Trying fallback check by email only');
        const emailOnlyResponse = await fetch(`${API_URL}/users/check-by-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ email: userEmail })
        });
        
        if (emailOnlyResponse.ok) {
          const emailCheckData = await emailOnlyResponse.json();
          if (emailCheckData.exists) {
            console.log('User exists in local database (found by email)');
            
            // Update user with clerk ID if needed
            try {
              await fetch(`${API_URL}/users/update-clerk-id`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ 
                  email: userEmail,
                  clerkId: clerkUserId 
                })
              });
              console.log('Updated user with Clerk ID');
            } catch (updateErr) {
              console.error('Failed to update user with Clerk ID:', updateErr);
              // Continue anyway as the user exists
            }
            
            return true;
          }
        }
      } catch (fallbackErr) {
        console.error('Fallback email check failed:', fallbackErr);
        // Continue to the invitation check
      }
      
      // If user doesn't exist in local DB, check for pending invitations
      console.log('Checking for pending invitations');
      const invitationResponse = await fetch(`${API_URL}/invitations/check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: userEmail })
      });
      
      if (invitationResponse.ok) {
        const invitationData = await invitationResponse.json();
        console.log('Invitation check response:', invitationData);
        
        if (invitationData.hasPendingInvitation) {
          // If there's a pending invitation, accept it automatically
          console.log('User has pending invitation. Accepting...');
          
          const acceptResponse = await fetch(`${API_URL}/invitations/accept-by-email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ 
              email: userEmail,
              clerkUserId 
            })
          });
          
          if (acceptResponse.ok) {
            console.log('Invitation accepted automatically');
            return true; // Invitation accepted
          } else {
            console.error('Failed to accept invitation automatically:', await acceptResponse.text());
            return false;
          }
        }
      } else {
        console.error('Error checking invitations:', await invitationResponse.text());
      }
      
      // User doesn't exist in local DB and has no pending invitations
      console.log('User does not have access to this instance');
      return false;
      
    } catch (error) {
      console.error('Error checking user access:', error);
      return false;
    }
  };

  // Handle user login
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateLoginForm()) {
      return;
    }

    if (!signIn) {
      setError('System error: Unable to initialize the login process.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await signIn.create({
        identifier: email,
        password,
      });
      
      if (result.status === "complete") {
        console.log("Login successful with Clerk");
        
        try {
          // Add a small delay before navigation to ensure smooth transition
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Activate the session to get the token
          await clerk.setActive({ session: result.createdSessionId });
          console.log("Session activated successfully, ID:", result.createdSessionId);
          
          // Get the session token
          const token = await clerk.session?.getToken();
          
          // Check if user has access to this instance
          if (!token) {
            setError("Failed to retrieve authentication token. Please try again.");
            setLoading(false);
            return;
          }
          
          // Extract user ID and primary email
          const clerkUserId = clerk.user?.id;
          const primaryEmail = clerk.user?.primaryEmailAddress?.emailAddress;
          
          if (!clerkUserId || !primaryEmail) {
            setError("Failed to retrieve user information. Please try again.");
            setLoading(false);
            return;
          }
          
          // Add a retry mechanism for the access check
          let hasAccess = false;
          let retryCount = 0;
          const maxRetries = 2;
          
          while (!hasAccess && retryCount <= maxRetries) {
            console.log(`Checking user access (attempt ${retryCount + 1}/${maxRetries + 1})`);
            hasAccess = await checkUserAccess(clerkUserId, primaryEmail, token);
            
            if (!hasAccess && retryCount < maxRetries) {
              // Wait briefly before retrying
              await new Promise(resolve => setTimeout(resolve, 1000));
              retryCount++;
            } else {
              break;
            }
          }
          
          if (hasAccess) {
            // User has access, add a small transition delay then navigate
            await new Promise(resolve => setTimeout(resolve, 200));
            navigate(redirectPath);
          } else {
            // Try one last check with the backend's self-healing endpoint
            try {
              console.log('Trying self-healing endpoint');
              const healResponse = await fetch(`${API_URL}/invitations/get-or-sync-user`, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'x-clerk-user-id': clerkUserId
                }
              });
              
              if (healResponse.ok) {
                console.log('Self-healing successful, proceeding with login');
                await new Promise(resolve => setTimeout(resolve, 200));
                navigate(redirectPath);
                return;
              }
            } catch (healError) {
              console.error('Self-healing failed:', healError);
            }
            
            // User doesn't have access to this instance
            // Sign out from Clerk since they shouldn't be logged in
            await clerk.signOut();
            
            setError(
              "You don't have access to this instance. Please contact an administrator to request an invitation."
            );
            setLoading(false);
          }
        } catch (sessionError) {
          console.error("Error activating session:", sessionError);
          setError("Error establishing session. Please try again.");
          setLoading(false);
        }
      } else {
        console.log("Incomplete login status:", result.status);
        setError('Unable to sign in with those credentials.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Error signing in. Please check your credentials and try again.');
      setLoading(false);
    }
  };

  // Navigate to sign up page
  const handleSignUp = () => {
    navigate(`/signup${redirectPath !== '/dashboard' ? `?redirect=${redirectPath}` : ''}`);
  };

  // Start password recovery process
  const startPasswordRecovery = (e: React.FormEvent) => {
    e.preventDefault();
    setIsRecoveringPassword(true);
    setError(null);
  };

  // Handle back to login
  const handleBackToLogin = () => {
    setIsRecoveringPassword(false);
    setCodeSent(false);
    setRecoveryCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
  };

  // Send password reset code
  const sendPasswordResetCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await signIn?.create({
        strategy: 'reset_password_email_code',
        identifier: email,
      });
      
      setCodeSent(true);
      setLoading(false);
    } catch (err: any) {
      console.error('Error sending reset code:', err);
      setError(err?.errors?.[0]?.longMessage || 'Failed to send password reset code. Please try again.');
      setLoading(false);
    }
  };

  // Reset password
  const resetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!recoveryCode.trim()) {
      setError('Please enter the verification code');
      return;
    }
    
    if (!newPassword) {
      setError('Please enter a new password');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await signIn?.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code: recoveryCode,
        password: newPassword,
      });
      
      if (result?.status === 'complete') {
        // Set the active session
        await clerk.setActive({ session: result.createdSessionId });
        
        // Reset form and redirect
        setIsRecoveringPassword(false);
        setCodeSent(false);
        navigate(redirectPath);
      } else {
        setError('Password reset was not completed. Please try again.');
        setLoading(false);
      }
    } catch (err: any) {
      console.error('Error resetting password:', err);
      setError(err?.errors?.[0]?.longMessage || 'Failed to reset password. Please try again.');
      setLoading(false);
    }
  };

  if (!isSignInLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box 
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f4f6f8"
      }}
    >
      <Header />

      <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 6 }, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {/* Welcome Card */}
        <Card 
          sx={{ 
            mb: { xs: 2, sm: 4 }, 
            background: theme.palette.primary.main,
            color: 'white',
            boxShadow: `0px 8px 20px ${theme.palette.primary.main}25`,
            borderRadius: 2
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
            <Box display="flex" alignItems="center" mb={1}>
              <LockOutlined sx={{ fontSize: { xs: 24, sm: 40 }, mr: { xs: 1, sm: 2 } }} />
              <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                {isRecoveringPassword ? "Reset Password" : "Welcome Back!"}
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }}>
              {isRecoveringPassword 
                ? "Enter your email to receive a password reset code."
                : `Sign in to your ${settings.companyName} account to access your transcriptions, summaries and more.`
              }
            </Typography>
          </CardContent>
        </Card>

        <Paper 
          elevation={3} 
          sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2 }}
        >
          {!isRecoveringPassword ? (
            <Box component="form" onSubmit={handleLogin}>
              <Typography variant="h5" gutterBottom fontWeight="600">
                Sign In
              </Typography>
              
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={!!formErrors.password}
                  helperText={formErrors.password}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Box textAlign="right" mt={1} mb={2}>
                  <Link 
                    href="#"
                    variant="body2" 
                    onClick={(e) => {
                      e.preventDefault();
                      startPasswordRecovery(e);
                    }}
                    color="primary"
                  >
                    Forgot your password?
                  </Link>
                </Box>
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  py: 1.5,
                  mb: 2
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
              </Button>

              
            </Box>
          ) : (
            <Box>
              {!codeSent ? (
                <Box component="form" onSubmit={sendPasswordResetCode}>
                  <Typography variant="h5" gutterBottom fontWeight="600">
                    Reset Password
                  </Typography>
                  
                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  )}
                  
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    variant="outlined"
                    sx={{ mb: 3 }}
                  />
                  
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    sx={{ py: 1.5, mb: 2 }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Code'}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleBackToLogin}
                    sx={{ py: 1.5 }}
                  >
                    Back to Login
                  </Button>
                </Box>
              ) : (
                <Box component="form" onSubmit={resetPassword}>
                  <Typography variant="h5" gutterBottom fontWeight="600">
                    Enter Verification Code
                  </Typography>
                  
                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  )}
                  
                  <Alert severity="info" sx={{ mb: 3 }}>
                    A verification code has been sent to your email address. Please check your inbox.
                  </Alert>
                  
                  <TextField
                    fullWidth
                    label="Verification Code"
                    value={recoveryCode}
                    onChange={(e) => setRecoveryCode(e.target.value)}
                    variant="outlined"
                    sx={{ mb: 2 }}
                    name="verification-code"
                    autoComplete="off"
                    inputProps={{ 
                      autoComplete: "new-password"
                    }}
                  />
                  
                  <TextField
                    fullWidth
                    label="New Password"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    variant="outlined"
                    sx={{ mb: 2 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleTogglePassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    variant="outlined"
                    sx={{ mb: 3 }}
                    error={newPassword !== confirmPassword && confirmPassword !== ''}
                    helperText={newPassword !== confirmPassword && confirmPassword !== '' ? 'Passwords do not match' : ''}
                  />
                  
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    sx={{ py: 1.5, mb: 2 }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleBackToLogin}
                    sx={{ py: 1.5 }}
                  >
                    Back to Login
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </Container>

      <Footer />
    </Box>
  );
};

export default Login;
